import React from "react";
import { useQuery } from "@tanstack/react-query";
import { ChartSyncNotice } from "./chartHelpers";
import { useSyncPolling } from "./syncState";
import {
  SGetUssdRatingTrends,
  SGetKoboRatingTrends,
  SGetDeviceRatingTrends,
} from "../../../apis/app.service";
import {
  BarChart,
  Bar,
  Cell,
  LabelList,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { colors } from "../../../theme/colors";

const RATING_CATEGORIES = [
  { key: "Very Bad", color: "#d32f2f" },
  { key: "Bad", color: "#f57c00" },
  { key: "Okay", color: "#fbc02d" },
  { key: "Good", color: "#7cb342" },
  { key: "Very Good", color: "#388e3c" },
];

interface SatisfactionTrendsProps {
  dataSource: string;
  organization: string | null;
  /** Kobo project id to restrict the data to a single survey */
  survey?: string;
  startDate: string;
  endDate: string;
  dateType: string;
  onRefetch?: (refetchFn: () => void) => void;
  onSyncStatusChange?: (isSyncing: boolean) => void;
}

function SatisfactionTrendsNew({
  dataSource,
  organization,
  survey,
  startDate,
  endDate,
  dateType,
  onRefetch,
  onSyncStatusChange,
}: SatisfactionTrendsProps) {
  // State for toggling between views
  const [showPercentageAverage, setShowPercentageAverage] =
    React.useState(true);

  const { refetchInterval, resolve, reset } = useSyncPolling();

  // Fetch USSD rating trends
  const { data: ussdRatingTrendsData, isLoading: ussdTrendsLoading } = useQuery(
    {
      queryKey: ["ussdRatingTrends", startDate, endDate, organization],
      queryFn: () =>
        SGetUssdRatingTrends({
          startDate,
          endDate,
          office: organization || undefined,
        }),
      enabled: dataSource === "USSD" || dataSource === "All",
    },
  );

  // Fetch Kobo rating trends with auto-refetch when syncing
  const {
    data: koboRatingTrendsData,
    isLoading: koboTrendsLoading,
    refetch: refetchKoboTrends,
  } = useQuery({
    queryKey: ["koboRatingTrends", startDate, endDate, organization, survey],
    queryFn: () =>
      SGetKoboRatingTrends({
        startDate,
        endDate,
        organization: organization || undefined,
        survey,
      }),
    enabled: dataSource === "Kobo" || dataSource === "All",
    refetchInterval,
    refetchOnWindowFocus: false,
  });

  // Pass refetch function to parent
  React.useEffect(() => {
    if (onRefetch) {
      onRefetch(refetchKoboTrends);
    }
  }, [onRefetch, refetchKoboTrends]);

  // Fetch Device rating trends
  const { data: deviceRatingTrendsData, isLoading: deviceTrendsLoading } =
    useQuery({
      queryKey: ["deviceRatingTrends", startDate, endDate, organization],
      queryFn: () =>
        SGetDeviceRatingTrends({
          startDate,
          endDate,
          institution: organization || undefined,
        }),
      enabled: dataSource === "Device" || dataSource === "All",
    });

  // Combine data based on source
  const ratingTrendsData = React.useMemo(() => {
    if (dataSource === "All") {
      const allData = [];
      if (
        ussdRatingTrendsData?.data &&
        Array.isArray(ussdRatingTrendsData.data)
      ) {
        const filtered = ussdRatingTrendsData.data.filter(
          (item) => !item._syncStatus,
        );
        allData.push(...filtered);
      }
      if (
        koboRatingTrendsData?.data &&
        Array.isArray(koboRatingTrendsData.data)
      ) {
        const filtered = koboRatingTrendsData.data.filter(
          (item) => !item._syncStatus,
        );
        allData.push(...filtered);
      }
      if (
        deviceRatingTrendsData?.data &&
        Array.isArray(deviceRatingTrendsData.data)
      ) {
        const filtered = deviceRatingTrendsData.data.filter(
          (item) => !item._syncStatus,
        );
        allData.push(...filtered);
      }
      return { data: allData };
    }

    return dataSource === "USSD"
      ? ussdRatingTrendsData
      : dataSource === "Kobo"
        ? koboRatingTrendsData
        : deviceRatingTrendsData;
  }, [
    dataSource,
    ussdRatingTrendsData,
    koboRatingTrendsData,
    deviceRatingTrendsData,
  ]);

  // Sync state of the Kobo feed: still building, failed, or outlasted the poll budget.
  const sync =
    dataSource === "Kobo" || dataSource === "All"
      ? resolve(koboRatingTrendsData)
      : ({ state: "ready" } as ReturnType<typeof resolve>);

  const isSyncing = sync.state === "syncing";
  const syncEnded = sync.state === "failed" || sync.state === "stalled";

  const retrySync = React.useCallback(() => {
    reset();
    refetchKoboTrends();
  }, [reset, refetchKoboTrends]);

  // Report syncing status to parent
  React.useEffect(() => {
    if (onSyncStatusChange) {
      onSyncStatusChange(isSyncing);
    }
  }, [onSyncStatusChange, isSyncing]);

  const trendsLoading =
    dataSource === "All"
      ? ussdTrendsLoading ||
        koboTrendsLoading ||
        deviceTrendsLoading ||
        isSyncing
      : dataSource === "USSD"
        ? ussdTrendsLoading
        : dataSource === "Kobo"
          ? koboTrendsLoading || isSyncing
          : deviceTrendsLoading;

  // Time-series data for the percentage average line chart
  const lineChartData = React.useMemo(() => {
    if (!ratingTrendsData?.data) return [];

    const dataByDate: Record<string, any> = {};

    ratingTrendsData.data
      .filter((item: any) => !item._syncStatus)
      .forEach((item: any) => {
        const date = item.date;
        if (!dataByDate[date]) {
          dataByDate[date] = {
            date,
            "Very Bad": 0,
            Bad: 0,
            Okay: 0,
            Good: 0,
            "Very Good": 0,
          };
        }
        dataByDate[date][item.rating] = item.count;
      });

    const calcAvg = (entry: any) => {
      const vb = entry["Very Bad"];
      const b = entry["Bad"];
      const o = entry["Okay"];
      const g = entry["Good"];
      const vg = entry["Very Good"];
      const total = vb + b + o + g + vg;
      return total > 0
        ? parseFloat(
            ((vb * 0 + b * 25 + o * 50 + g * 75 + vg * 100) / total).toFixed(2),
          )
        : null;
    };

    // Weekly view: scaffold all 7 days; days without records get null (not 0)
    if (dateType === "week") {
      const result: any[] = [];
      const [sy, sm, sd] = startDate.split("-").map(Number);
      const [ey, em, ed] = endDate.split("-").map(Number);
      const current = new Date(sy, sm - 1, sd);
      const endD = new Date(ey, em - 1, ed);
      while (current <= endD) {
        const y = current.getFullYear();
        const mo = String(current.getMonth() + 1).padStart(2, "0");
        const d = String(current.getDate()).padStart(2, "0");
        const dateStr = `${y}-${mo}-${d}`;
        result.push({
          date: dateStr,
          "Average Satisfaction": dataByDate[dateStr]
            ? calcAvg(dataByDate[dateStr])
            : null,
        });
        current.setDate(current.getDate() + 1);
      }
      return result;
    }

    return Object.values(dataByDate)
      .map((entry: any) => ({
        date: entry.date,
        "Average Satisfaction": calcAvg(entry),
      }))
      .sort(
        (a: any, b: any) =>
          new Date(a.date).getTime() - new Date(b.date).getTime(),
      );
  }, [ratingTrendsData, dateType, startDate, endDate]);

  // Aggregated totals per rating for the category breakdown bar chart
  const categoryBarData = React.useMemo(() => {
    const totals: Record<string, number> = {
      "Very Bad": 0,
      Bad: 0,
      Okay: 0,
      Good: 0,
      "Very Good": 0,
    };
    if (ratingTrendsData?.data) {
      ratingTrendsData.data
        .filter((item: any) => !item._syncStatus)
        .forEach((item: any) => {
          if (totals[item.rating] !== undefined)
            totals[item.rating] += item.count;
        });
    }
    const total = Object.values(totals).reduce((s, v) => s + v, 0);
    return RATING_CATEGORIES.map((cat) => ({
      name: cat.key,
      count: totals[cat.key],
      color: cat.color,
      pct:
        total > 0
          ? parseFloat(((totals[cat.key] / total) * 100).toFixed(1))
          : 0,
    }));
  }, [ratingTrendsData]);

  // Calculate summary statistics
  const summaryStats = React.useMemo(() => {
    if (!ratingTrendsData?.data) return null;

    const data = ratingTrendsData.data.filter((item: any) => !item._syncStatus);
    const total = data.reduce((sum: number, item: any) => sum + item.count, 0);

    const ratingWeights: Record<string, number> = {
      "Very Bad": 0,
      Bad: 25,
      Okay: 50,
      Good: 75,
      "Very Good": 100,
    };

    const weightedSum = data.reduce((sum: number, item: any) => {
      const weight = ratingWeights[item.rating] ?? 0;
      return sum + item.count * weight;
    }, 0);

    const percentageAverage = total > 0 ? weightedSum / total : 0;

    return {
      total,
      positivePercentage: percentageAverage.toFixed(2),
    };
  }, [ratingTrendsData]);

  const tickFormatter = (date: string) => {
    const dateStr = date ? date.substring(0, 10) : "";
    const dateObj = new Date(dateStr + "T12:00:00");
    if (dateType === "week") {
      return dateObj.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
      });
    }
    if (dateType === "month") {
      return dateObj.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    }
    if (dateStr.endsWith("-01")) {
      return dateObj.toLocaleDateString("en-US", {
        month: "short",
        year: "numeric",
      });
    }
    return dateObj.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  const labelFormatter = (date: string) => {
    const dateStr = date ? date.substring(0, 10) : "";
    const dateObj = new Date(dateStr + "T12:00:00");
    if (dateType === "week") {
      return dateObj.toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
      });
    }
    if (dateType === "month") {
      return dateObj.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
      });
    }
    if (dateStr.endsWith("-01")) {
      return dateObj.toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      });
    }
    return dateObj.toLocaleDateString();
  };

  const hasData = showPercentageAverage
    ? lineChartData.some((d) => d["Average Satisfaction"] !== null)
    : categoryBarData.some((d) => d.count > 0);

  return (
    <div
      style={{
        backgroundColor: "white",
        borderRadius: "20px",
        border: "1px solid #e0e0e0",
        overflow: "hidden",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      }}
    >
      {/* header */}
      <div
        style={{
          display: "flex",
          borderBottom: "1px solid #e0e0e0",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: colors.bgPrimary,
        }}
      >
        {/* heading */}
        <div
          style={{
            padding: "1.25rem 2rem",
          }}
        >
          <h3 style={{ margin: 0, fontSize: "1.25rem", color: "#e0e0e0" }}>
            Customer Satisfaction Trends
          </h3>
          <p
            style={{
              margin: "0.25rem 0 0 0",
              fontSize: "0.875rem",
              color: "#e0e0e0",
            }}
          >
            {summaryStats
              ? `${summaryStats.total.toLocaleString()} total responses • ${
                  summaryStats.positivePercentage
                }% average satisfaction`
              : "Track satisfaction ratings over time"}
          </p>
        </div>

        {/* Toggle switch */}
        <div
          style={{
            padding: "1.25rem 2rem",
            display: "flex",
            alignItems: "center",
            gap: "0.75rem",
          }}
        >
          <span
            style={{
              fontSize: "0.875rem",
              color: "#e0e0e0",
              fontWeight: 500,
            }}
          >
            Category Breakdown
          </span>
          <label
            style={{
              position: "relative",
              display: "inline-block",
              width: "48px",
              height: "24px",
            }}
          >
            <input
              type="checkbox"
              checked={showPercentageAverage}
              onChange={(e) => setShowPercentageAverage(e.target.checked)}
              style={{
                opacity: 0,
                width: 0,
                height: 0,
              }}
            />
            <span
              style={{
                position: "absolute",
                cursor: "pointer",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: showPercentageAverage
                  ? colors.semaOrange
                  : "#ccc",
                transition: "0.3s",
                borderRadius: "24px",
              }}
            >
              <span
                style={{
                  position: "absolute",
                  content: "",
                  height: "18px",
                  width: "18px",
                  left: showPercentageAverage ? "26px" : "3px",
                  bottom: "3px",
                  backgroundColor: "white",
                  transition: "0.3s",
                  borderRadius: "50%",
                }}
              />
            </span>
          </label>
          <span
            style={{
              fontSize: "0.875rem",
              color: "#e0e0e0",
              fontWeight: 500,
            }}
          >
            Percentage Average
          </span>
        </div>
      </div>

      {/* graph */}
      <div
        style={{
          paddingLeft: "2rem",
          paddingRight: "3rem",
          paddingTop: "3rem",
          minHeight: "600px",
        }}
      >
        {(dataSource === "USSD" ||
          dataSource === "Kobo" ||
          dataSource === "Device" ||
          dataSource === "All") && (
          <>
            {trendsLoading ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "2rem",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "0.75rem",
                }}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    border: "3px solid #e0e0e0",
                    borderTop: "3px solid #1976d2",
                    borderRadius: "50%",
                    animation: "spin 0.8s linear infinite",
                  }}
                />
                <div style={{ fontWeight: 500, color: "#333" }}>
                  {isSyncing ? "Syncing data from Kobo…" : "Loading trends…"}
                </div>
                {isSyncing && (
                  <div
                    style={{ fontSize: "0.8rem", color: "#888", maxWidth: 320 }}
                  >
                    Fetching and caching this period for the first time. This
                    will be much faster on your next visit.
                  </div>
                )}
              </div>
            ) : syncEnded ? (
              <ChartSyncNotice
                state={sync.state as "failed" | "stalled"}
                message={sync.message}
                onRetry={retrySync}
              />
            ) : hasData ? (
              showPercentageAverage ? (
                // Percentage Average: time-series line chart
                <ResponsiveContainer width="100%" height={500}>
                  <LineChart data={lineChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tickFormatter={tickFormatter} />
                    <YAxis
                      domain={[0, 100]}
                      label={{
                        value: "Satisfaction %",
                        angle: -90,
                        position: "insideLeft",
                      }}
                    />
                    <Tooltip
                      labelFormatter={labelFormatter}
                      formatter={(value: any, name: string) => [
                        `${value}%`,
                        name,
                      ]}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="Average Satisfaction"
                      stroke={colors.semaOrange}
                      strokeWidth={3}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                      name="Average Satisfaction"
                      connectNulls
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                // Category Breakdown: aggregated bar chart (one bar per rating)
                <ResponsiveContainer width="100%" height={500}>
                  <BarChart
                    data={categoryBarData}
                    barCategoryGap="30%"
                    margin={{ top: 30, right: 0, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip
                      formatter={(value: any) => [
                        value.toLocaleString(),
                        "Responses",
                      ]}
                    />
                    <Bar dataKey="count" name="Responses" radius={[4, 4, 0, 0]}>
                      {categoryBarData.map((entry) => (
                        <Cell key={entry.name} fill={entry.color} />
                      ))}
                      <LabelList
                        dataKey="pct"
                        position="top"
                        formatter={(v: any) => `${v}%`}
                        style={{
                          fontSize: "0.8rem",
                          fontWeight: 600,
                          fill: "#333",
                        }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )
            ) : (
              <div style={{ textAlign: "center", padding: "2rem" }}>
                No data available for the selected filters
              </div>
            )}
          </>
        )}
        {!dataSource && (
          <div style={{ textAlign: "center", padding: "2rem", color: "#666" }}>
            Select a data source to view satisfaction trends
          </div>
        )}
      </div>
    </div>
  );
}

export default SatisfactionTrendsNew;
